// src/components/asistencia/asistenciaEngine.js
// Motor de asistencia compartido: LEE los datos de Firestore (SDK del navegador) y hace la cuenta con
// asistenciaCalculos.js. Lo usan Reporte, KPIs, Hoy, Horas Extras, Mi Asistencia, Evaluación del Desempeño,
// Solicitudes y Vacaciones.
// 🔴 Desde el 06/10/2026 la CUENTA vive en asistenciaCalculos.js (clasificar cada día, el resumen de cada colaborador
// y los ayudantes de tiempo), que tiene una copia idéntica en functions/ para Lumi: un cambio de cálculo se hace AHÍ
// (y se copia tal cual al otro lado). Acá solo se lee. Los nombres de siempre se siguen exportando desde este archivo.
import { db } from '../../services/firebase';
import { collection, getDocs, getDoc, doc, query, where } from 'firebase/firestore';
import {
  pad, isoDe, ddmmyyyy, parseISO, dow, minutos, DIAS_NOMBRE, hora12, fmtMin, fmtDur, rangoDias,
  horarioDelDia, horarioVigente, esHorarioValido, esSinHoraFija, calcularHorasPermiso, clasificar, colaboradoresConHorario, armarAsistencia
} from './asistenciaCalculos';

export {
  pad, isoDe, ddmmyyyy, parseISO, dow, minutos, DIAS_NOMBRE, hora12, fmtMin, fmtDur, rangoDias,
  horarioDelDia, horarioVigente, esHorarioValido, esSinHoraFija, calcularHorasPermiso, clasificar
};
export const hoyGT = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Guatemala', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
export const inicioMesGT = () => `${hoyGT().slice(0, 7)}-01`;

// --- Feriados (asistencia_feriados) para conteos de días hábiles ---
// El doc del año trae: `feriados` (generales, aplican a TODOS) y `especiales`
// ([{fecha, nombre, usuarios:[emails]}], aplican SOLO a los colaboradores asignados —
// ej. Día de la Madre). Caché por año a nivel de módulo: cada año se lee UNA vez.
const _feriadosAnioCache = {};
async function _cargarAnioFeriados(a) {
  if (!_feriadosAnioCache[a]) {
    try {
      const s = await getDoc(doc(db, 'asistencia_feriados', a));
      _feriadosAnioCache[a] = {
        generales: (s.exists() && Array.isArray(s.data().feriados)) ? s.data().feriados.map((f) => f.fecha) : [],
        especiales: (s.exists() && Array.isArray(s.data().especiales)) ? s.data().especiales : []
      };
    } catch (e) { _feriadosAnioCache[a] = { generales: [], especiales: [] }; }
  }
  return _feriadosAnioCache[a];
}
// FeriadosTab la llama al guardar, para que la sesión vea los cambios al instante.
export function limpiarFeriadosCache(anio) { delete _feriadosAnioCache[String(anio)]; }

export async function feriadosSet(anios) {
  const set = new Set();
  for (const a of anios.map(String)) {
    const d = await _cargarAnioFeriados(a); // eslint-disable-line no-await-in-loop
    d.generales.forEach((f) => set.add(f));
  }
  return set;
}

// Generales + especiales del año (para que el consumidor filtre por colaborador)
export async function feriadosDataAnios(anios) {
  const generales = new Set();
  const especiales = [];
  for (const a of anios.map(String)) {
    const d = await _cargarAnioFeriados(a); // eslint-disable-line no-await-in-loop
    d.generales.forEach((f) => generales.add(f));
    especiales.push(...d.especiales);
  }
  return { generales, especiales };
}

// Días hábiles de un rango: lunes a viernes, EXCLUYENDO los feriados generales y,
// si se pasa el email, también los feriados ESPECIALES asignados a ese colaborador.
// (Lo usan Solicitudes y Vacaciones para descontar vacaciones correctamente.)
export async function contarDiasHabiles(desde, hasta, email) {
  if (!desde || !hasta || hasta < desde) return 0;
  const { generales, especiales } = await feriadosDataAnios([desde.slice(0, 4), hasta.slice(0, 4)]);
  const em = (email || '').toLowerCase();
  const espSet = new Set(
    em ? especiales.filter((e) => (e.usuarios || []).some((u) => (u || '').toLowerCase() === em)).map((e) => e.fecha) : []
  );
  let n = 0;
  rangoDias(desde, hasta).forEach((f) => { const d = dow(f); if (d >= 1 && d <= 5 && !generales.has(f) && !espSet.has(f)) n++; });
  return n;
}

// Devuelve { filas: [{usuarioId,email,nombre,deptoId,deptoNom,horario,resumen,detalleDias}], diasRango, trackedCount }.
// Lee lo de siempre (las mismas consultas, en el mismo orden y con los mismos `try`) y hace la cuenta con
// colaboradoresConHorario + armarAsistencia (asistenciaCalculos.js). Lumi lee con las mismas consultas en el servidor.
export async function computeAsistencia({ desde, hasta, filtroDepto, usuariosById, deptoNombre, soloEmail }) {
  const _soloEmail = (soloEmail || '').toLowerCase();
  const diasArr = rangoDias(desde, hasta);

  // Exclusiones configuradas (socios/gerentes, etc.)
  let exclusiones = null;
  try { const ex = await getDoc(doc(db, 'asistencia_config', 'exclusiones')); if (ex.exists()) exclusiones = ex.data(); } catch (e) { /* noop */ }

  // Con soloEmail (Mi Asistencia) se lee SOLO el expediente propio: 1 lectura en
  // vez de toda la colección, y las reglas de seguridad solo permiten el propio
  // a quien no es gestor (jun 2026). Las vistas de gestores siguen leyendo todo.
  let expDocs;
  if (_soloEmail) {
    const propioId = Object.keys(usuariosById).find(
      (id) => ((usuariosById[id]?.email || '').toLowerCase() === _soloEmail)
    );
    const expDoc = propioId ? await getDoc(doc(db, 'expedientes', propioId)) : null;
    expDocs = (expDoc && expDoc.exists()) ? [expDoc] : [];
  } else {
    expDocs = (await getDocs(collection(db, 'expedientes'))).docs;
  }
  const tracked = colaboradoresConHorario({
    expedientes: expDocs.map((d) => ({ id: d.id, data: d.data() })),
    usuariosById, exclusiones, soloEmail: _soloEmail, filtroDepto
  });
  if (tracked.length === 0) return { filas: [], diasRango: diasArr.length, trackedCount: 0 };

  // Con soloEmail se filtran las marcas propias en la query: las reglas de
  // asistencia_marcas solo dejan a un colaborador común leer SUS marcas (una
  // query sin ese filtro se rechaza entera) y además se leen muchísimas menos.
  // Requiere índice compuesto usuarioEmail ASC + fecha ASC (se crea en consola).
  const marcasSnap = _soloEmail
    ? await getDocs(query(collection(db, 'asistencia_marcas'), where('usuarioEmail', '==', _soloEmail), where('fecha', '>=', desde), where('fecha', '<=', hasta)))
    : await getDocs(query(collection(db, 'asistencia_marcas'), where('fecha', '>=', desde), where('fecha', '<=', hasta)));
  const feriadosPorAnio = {};
  await Promise.all([...new Set([desde.slice(0, 4), hasta.slice(0, 4)])].map(async (a) => {
    const fs = await getDoc(doc(db, 'asistencia_feriados', a));
    feriadosPorAnio[a] = fs.exists() ? fs.data() : null;
  }));

  // Cada colección en su `try`, como siempre: si una lectura falla (reglas, red), se sigue sin ella.
  const leer = async (consulta) => {
    try { return (await getDocs(consulta())).docs.map((d) => d.data()); } catch (e) { return []; }
  };
  const solicitudesVacaciones = await leer(() => query(collection(db, 'solicitudes_vacaciones'), where('fechaFin', '>=', desde)));
  const registrosVacaciones = await leer(() => collection(db, 'registros_vacaciones'));
  const incapacidades = await leer(() => query(collection(db, 'asistencia_incapacidades'), where('fechaFin', '>=', desde)));
  // Home office: en Mi Asistencia (soloEmail) se filtra por el email propio para respetar las reglas de seguridad
  // y leer solo lo del colaborador; el gestor lee el rango completo.
  const homeOffice = await leer(() => (_soloEmail
    ? query(collection(db, 'asistencia_home_office'), where('usuarioEmail', '==', _soloEmail))
    : query(collection(db, 'asistencia_home_office'), where('fechaFin', '>=', desde))));
  const omisiones = await leer(() => query(collection(db, 'asistencia_omisiones'), where('fechaFin', '>=', desde)));
  const horariosEspeciales = await leer(() => query(collection(db, 'asistencia_horarios_especiales'), where('fechaFin', '>=', desde)));

  return armarAsistencia({
    desde, hasta, tracked, deptoNombre,
    datos: {
      marcas: marcasSnap.docs.map((d) => d.data()),
      feriadosPorAnio, solicitudesVacaciones, registrosVacaciones, incapacidades, homeOffice, omisiones, horariosEspeciales
    }
  });
}
